/**
 * Mobile PWA Scan Surface — offline-first camera + RFID scanning for field crew.
 *
 * Features:
 *  • Camera QR scanning via `html5-qrcode` (works on any modern mobile browser)
 *  • Connected RFID reader adapter — WebSerial (Impinj-class fixed reader) +
 *    WebBluetooth (Zebra RFD40-class handheld). Real hardware SDKs plug into
 *    the adapter callbacks; MOCKED test-tag button is available for offline
 *    validation.
 *  • Offline-first via IndexedDB queue — scans persist locally when offline
 *    and auto-flush to `POST /mtx/scans/batch` on reconnect.
 *  • Auto-detects online/offline state and dispatches the queue.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { QRCodeCanvas } from 'qrcode.react';
import { Camera, Wifi, WifiOff, Radio, Trash2, Send, ScanLine,
         Loader2, Check, X, Bluetooth, Cable } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '@/lib/api';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

// ── IndexedDB helpers (single object store: `scans`) ──────────────────────
const DB_NAME = 'mtx-scan-queue';
const STORE = 'scans';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function enqueue(scan) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).add({ ...scan, enqueuedAt: new Date().toISOString() });
    tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error);
  });
}

async function readAll() {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => res(req.result || []); req.onerror = () => rej(req.error);
  });
}

async function clearIds(ids) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    const os = tx.objectStore(STORE);
    ids.forEach(id => os.delete(id));
    tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error);
  });
}

// ── RFID adapter (WebBluetooth + WebSerial) ───────────────────────────────
// Real Zebra RFD40 SDK exposes `characteristicValueChanged` GATT events;
// real Impinj R700 exposes a JSON-lines-over-serial stream. These adapters
// wire the browser transport layer; hardware handshake code plugs into
// `onTag` callbacks.
class RfidBluetooth {
  constructor(onTag) { this.onTag = onTag; this.device = null; }
  async connect() {
    if (!navigator.bluetooth) throw new Error('WebBluetooth not supported');
    this.device = await navigator.bluetooth.requestDevice({
      filters: [{ namePrefix: 'RFD40' }, { namePrefix: 'Zebra' }],
      optionalServices: ['battery_service'],
    });
    return this.device;
  }
  disconnect() { this.device?.gatt?.disconnect(); this.device = null; }
}

class RfidSerial {
  constructor(onTag) { this.onTag = onTag; this.port = null; this.reader = null; }
  async connect() {
    if (!navigator.serial) throw new Error('WebSerial not supported');
    this.port = await navigator.serial.requestPort();
    await this.port.open({ baudRate: 115200 });
    this.reader = this.port.readable.getReader();
    const dec = new TextDecoder();
    let buf = '';
    while (this.port.readable) {
      const { value, done } = await this.reader.read();
      if (done) break;
      buf += dec.decode(value);
      const lines = buf.split('\n'); buf = lines.pop();
      for (const line of lines) {
        try {
          const j = JSON.parse(line);
          if (j.epc) this.onTag(`RFID-${j.epc.toUpperCase()}`);
        } catch { /* skip malformed */ }
      }
    }
  }
  async disconnect() {
    try { await this.reader?.cancel(); } catch { /* ignore */ }
    try { await this.port?.close(); } catch { /* ignore */ }
    this.port = null;
  }
}

// ── UI ────────────────────────────────────────────────────────────────────
export default function MobileScan() {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get('project') || '';
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);
  const [flushing, setFlushing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [rfidMode, setRfidMode] = useState('OFF');   // OFF | BT | SERIAL
  const [lastRead, setLastRead] = useState(null);
  const [ips, setIps] = useState([]);
  const [selectedIp, setSelectedIp] = useState('');
  const qrRef = useRef(null);
  const readerRef = useRef(null);
  const btRef = useRef(null);
  const serialRef = useRef(null);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const refreshQueue = useCallback(async () => setQueue(await readAll()), []);
  useEffect(() => { refreshQueue(); }, [refreshQueue]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/interface-points`);
        setIps(data.interfacePoints || []);
        if (data.interfacePoints?.length) setSelectedIp(data.interfacePoints[0].id);
      } catch { /* silent */ }
    })();
  }, [projectId]);

  const handleTag = useCallback(async (code) => {
    setLastRead({ code, at: new Date().toISOString() });
    if (!selectedIp) { toast.error('Pick an interface point first'); return; }
    if (!code || !projectId) return;
    // Resolve unit id from QR / RFID
    let unitId = code;
    if (online) {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/material-units/lookup?q=${encodeURIComponent(code)}`);
        if (data.match) unitId = data.match.id;
      } catch { /* keep raw code — will be resolved when queue flushes */ }
    }
    const scan = { materialUnitId: unitId, rawCode: code,
                    interfacePointId: selectedIp };
    // Try to push live if online; else queue
    if (online) {
      try {
        await api.post(`/v1/projects/${projectId}/mtx/scan`, scan);
        toast.success(`Scan submitted · ${code.slice(0, 20)}`);
        return;
      } catch (e) {
        toast.warning(`Live scan failed, queued locally: ${e.response?.data?.detail || 'network'}`);
      }
    }
    await enqueue(scan);
    toast(`Queued (offline · ${code.slice(0, 20)})`);
    refreshQueue();
  }, [projectId, selectedIp, online, refreshQueue]);

  const startCamera = async () => {
    if (scanning) return;
    try {
      const q = new Html5Qrcode('qr-reader');
      readerRef.current = q;
      await q.start({ facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => { handleTag(decoded); },
        () => { /* frame w/o QR — ignore */ });
      setScanning(true);
      // Detect torch capability so the toggle appears only when supported
      try {
        const track = q.getRunningTrackCameraCapabilities?.() ||
                       q.getRunningTrackSettings?.();
        // html5-qrcode 2.3+ exposes torch via applyVideoConstraints
        const caps = q?.getRunningTrackCameraCapabilities?.();
        setTorchSupported(!!(caps && (caps.torchFeature?.() || caps.torch)));
      } catch { setTorchSupported(false); }
    } catch (e) {
      toast.error(`Camera denied: ${e.message || e}`);
    }
  };
  const toggleTorch = async () => {
    const q = readerRef.current;
    if (!q) return;
    try {
      // html5-qrcode >=2.3 exposes applyVideoConstraints for torch
      await q.applyVideoConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (e) {
      toast.error('Torch not supported on this device');
      setTorchSupported(false);
    }
  };
  const stopCamera = async () => {
    try { await readerRef.current?.stop(); } catch { /* ignore */ }
    try { await readerRef.current?.clear(); } catch { /* ignore */ }
    setScanning(false);
  };

  const connectBt = async () => {
    try {
      btRef.current = new RfidBluetooth(handleTag);
      const dev = await btRef.current.connect();
      setRfidMode('BT');
      toast.success(`Bluetooth RFID paired: ${dev.name}`);
    } catch (e) {
      toast.error(`BT pair failed: ${e.message || 'not supported'}`);
    }
  };
  const connectSerial = async () => {
    try {
      serialRef.current = new RfidSerial(handleTag);
      // fire-and-forget: connect + start read loop
      serialRef.current.connect().catch(() => {});
      setRfidMode('SERIAL');
      toast.success('Serial RFID reader connected');
    } catch (e) {
      toast.error(`Serial connect failed: ${e.message || 'not supported'}`);
    }
  };
  const disconnectRfid = async () => {
    try { btRef.current?.disconnect(); } catch { /* ignore */ }
    try { await serialRef.current?.disconnect(); } catch { /* ignore */ }
    setRfidMode('OFF');
  };
  const fakeRfid = () => {
    // MOCKED — for offline validation without paired hardware
    const fakeEpc = `RFID-${Math.random().toString(16).slice(2, 14).toUpperCase()}`;
    handleTag(fakeEpc);
  };

  const flush = async () => {
    if (!online) { toast.error('Offline — reconnect first'); return; }
    setFlushing(true);
    try {
      const rows = await readAll();
      if (rows.length === 0) { toast('Queue is empty'); return; }
      const { data } = await api.post(
        `/v1/projects/${projectId}/mtx/scans/batch`,
        { scans: rows.map(r => ({
          materialUnitId: r.materialUnitId,
          interfacePointId: r.interfacePointId,
          notes: r.rawCode ? `offline-queued raw=${r.rawCode}` : undefined })) });
      const ok = data.results.map((r, i) => r.ok ? rows[i].id : null).filter(Boolean);
      if (ok.length) await clearIds(ok);
      toast.success(`Flushed ${data.succeeded} / ${data.total} scans`);
      refreshQueue();
    } catch (e) {
      toast.error(`Flush failed: ${e.message || 'network'}`);
    } finally { setFlushing(false); }
  };

  const clearAll = async () => {
    const rows = await readAll();
    await clearIds(rows.map(r => r.id));
    refreshQueue();
    toast('Queue cleared');
  };

  useEffect(() => {   // auto-flush when we come back online
    if (online && queue.length > 0 && !flushing) flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  return (
    <div className="min-h-screen p-3" style={{ background: '#0F172A', color: '#fff' }}>
      <Toaster position="top-center" richColors />
      <div className="max-w-md mx-auto space-y-3" data-testid="mobile-scan-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Field Scan · Mobile</div>
            <div className="text-[10px]" style={{ color: '#94A3B8' }}>Project: {projectId || '(pass ?project=)'}</div>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-bold"
            style={{ color: online ? C.success : C.danger }}
            data-testid={`online-indicator-${online ? 'on' : 'off'}`}>
            {online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {online ? 'Online' : 'Offline'}
          </div>
        </div>

        <div className="rounded-lg p-2" style={{ background: '#1E293B' }}>
          <div className="text-[10px] mb-1" style={{ color: '#94A3B8' }}>Interface Point</div>
          <select value={selectedIp} onChange={e => setSelectedIp(e.target.value)}
            data-testid="mobile-ip-select"
            className="w-full px-2 py-1.5 rounded text-[11px]"
            style={{ background: '#0F172A', color: '#fff', border: '1px solid #334155' }}>
            <option value="">-- pick interface --</option>
            {ips.map(ip => (
              <option key={ip.id} value={ip.id}>{ip.label || ip.type}</option>
            ))}
          </select>
        </div>

        {/* Camera scanner */}
        <div className="rounded-lg overflow-hidden" style={{ background: '#1E293B' }}>
          <div id="qr-reader" style={{ minHeight: scanning ? 300 : 0, background: '#000' }} />
          <div className="p-2 flex gap-2">
            {!scanning ? (
              <button onClick={startCamera} data-testid="start-camera"
                className="flex-1 py-2 rounded font-bold text-[11px] flex items-center justify-center gap-1"
                style={{ background: C.accent, color: '#fff' }}>
                <Camera className="w-4 h-4" /> Start Camera QR
              </button>
            ) : (
              <>
                <button onClick={stopCamera} data-testid="stop-camera"
                  className="flex-1 py-2 rounded font-bold text-[11px]"
                  style={{ background: C.slate, color: '#fff', border: '1px solid #334155' }}>
                  Stop Camera
                </button>
                <button onClick={toggleTorch} data-testid="torch-toggle"
                  className="px-3 py-2 rounded font-bold text-[11px] flex items-center gap-1"
                  style={{ background: torchOn ? C.warn : '#334155',
                            color: '#fff', border: '1px solid #334155' }}>
                  <span style={{ fontSize: 14 }}>{torchOn ? '🔦' : '🕯️'}</span>
                  {torchOn ? 'Torch On' : 'Torch'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* RFID */}
        <div className="rounded-lg p-2" style={{ background: '#1E293B' }}>
          <div className="text-[10px] uppercase font-bold mb-2" style={{ color: '#94A3B8' }}>
            RFID Reader · {rfidMode === 'OFF' ? 'Disconnected' : rfidMode}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={connectBt} disabled={rfidMode !== 'OFF'}
              data-testid="rfid-bt-connect"
              className="py-2 rounded text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-40"
              style={{ background: '#334155', color: '#fff' }}>
              <Bluetooth className="w-3.5 h-3.5" /> Zebra RFD40 (BT)
            </button>
            <button onClick={connectSerial} disabled={rfidMode !== 'OFF'}
              data-testid="rfid-serial-connect"
              className="py-2 rounded text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-40"
              style={{ background: '#334155', color: '#fff' }}>
              <Cable className="w-3.5 h-3.5" /> Impinj R700 (Serial)
            </button>
          </div>
          <div className="flex gap-2 mt-2">
            {rfidMode !== 'OFF' && (
              <button onClick={disconnectRfid} data-testid="rfid-disconnect"
                className="flex-1 py-1.5 rounded text-[10px]" style={{ background: C.danger, color: '#fff' }}>
                Disconnect
              </button>
            )}
            <button onClick={fakeRfid} data-testid="rfid-mock-read"
              className="flex-1 py-1.5 rounded text-[10px] font-bold"
              style={{ background: '#7C3AED', color: '#fff' }}>
              MOCK Tag Read (test)
            </button>
          </div>
        </div>

        {/* Last read display */}
        {lastRead && (
          <div className="rounded-lg p-2" style={{ background: '#1E293B' }}>
            <div className="text-[10px]" style={{ color: '#94A3B8' }}>Last Read</div>
            <div className="text-[11px] font-mono truncate" data-testid="last-read">{lastRead.code}</div>
            <div className="text-[9px]" style={{ color: '#64748B' }}>{new Date(lastRead.at).toLocaleTimeString()}</div>
          </div>
        )}

        {/* Offline queue */}
        <div className="rounded-lg p-2" style={{ background: '#1E293B' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase font-bold" style={{ color: '#94A3B8' }}>
              Offline Queue ({queue.length})
            </div>
            <div className="flex gap-1">
              <button onClick={flush} disabled={flushing || queue.length === 0}
                data-testid="flush-queue"
                className="px-2 py-1 rounded text-[10px] font-bold disabled:opacity-40"
                style={{ background: C.success, color: '#fff' }}>
                {flushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 inline mr-0.5" /> Flush</>}
              </button>
              <button onClick={clearAll} disabled={queue.length === 0}
                data-testid="clear-queue"
                className="px-2 py-1 rounded text-[10px] font-bold disabled:opacity-40"
                style={{ background: C.danger, color: '#fff' }}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto">
            {queue.map((q, i) => (
              <div key={i} className="text-[9px] p-1 font-mono truncate" style={{ borderBottom: '1px solid #334155' }}>
                {q.materialUnitId?.slice(-12)} → {ips.find(ip => ip.id === q.interfacePointId)?.type || q.interfacePointId?.slice(-6)}
              </div>
            ))}
            {queue.length === 0 && (
              <div className="text-[10px] italic" style={{ color: '#64748B' }}>Queue is empty</div>
            )}
          </div>
        </div>

        <div className="text-[9px] text-center" style={{ color: '#64748B' }}>
          PWA · offline-first · auto-flushes on reconnect · install to home-screen for full-screen mode
        </div>
      </div>
    </div>
  );
}
